'use client';
// AmanChain — unified application shell (English, LTR)
import { useState, useEffect } from 'react';
import {
  ShieldCheck, LayoutDashboard, Wallet, Send, Pickaxe, Gem, Vault, Search, FileText, Atom,
  ArrowLeftRight, TrendingUp, Building2, FlaskConical, Loader2,
  ShieldHalf, Code2, Bot, Link2,
} from 'lucide-react';
import { fetchJson, usePoll, fmtInt, Mono } from '@/components/chain/shared';
import type { Net, NetStats, MiningStatus, MarketTicker, StatusData } from '@/components/chain/types';
import { SessionProvider, useSession, AccountBar } from '@/components/chain/session';
import { ThemeToggle } from '@/components/chain/theme-provider';
import { Dashboard } from '@/components/chain/dashboard';
import { Wallets } from '@/components/chain/wallets';
import { Send as SendSection } from '@/components/chain/send';
import { Mining } from '@/components/chain/mining';
import { TokenLab } from '@/components/chain/tokens';
import { Staking } from '@/components/chain/staking';
import { Explorer } from '@/components/chain/explorer';
import { Whitepaper } from '@/components/chain/whitepaper';
import { QuantumLab } from '@/components/chain/quantum-lab';
import { DEX } from '@/components/chain/market';
import { InvestorHub } from '@/components/chain/investor-hub';
import { AdminConsole } from '@/components/chain/admin';
import { Developers } from '@/components/chain/developers';
import { AgentMarket } from '@/components/chain/agents';
import { Bridge } from '@/components/chain/bridge';
import { LaunchScreen } from '@/components/chain/launch';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'quantum', label: 'Quantum Shield', icon: Atom },
  { id: 'dex', label: 'DEX', icon: ArrowLeftRight },
  { id: 'bridge', label: 'Bridge', icon: Link2 },
  { id: 'agents', label: 'AI Agents', icon: Bot },
  { id: 'investors', label: 'Investors', icon: TrendingUp },
  { id: 'wallets', label: 'My Wallet', icon: Wallet },
  { id: 'send', label: 'Send', icon: Send },
  { id: 'mining', label: 'Mining', icon: Pickaxe },
  { id: 'tokens', label: 'Token Lab', icon: Gem },
  { id: 'staking', label: 'Staking', icon: Vault },
  { id: 'explorer', label: 'Explorer', icon: Search },
  { id: 'developers', label: 'Developers', icon: Code2 },
  { id: 'admin', label: 'Admin', icon: ShieldHalf },
  { id: 'whitepaper', label: 'Whitepaper', icon: FileText },
];

// Tabs that are Mainnet features
const MAINNET_ONLY = new Set(['investors']);

export default function Home() {
  const [tab, setTab] = useState('dashboard');
  const [net, setNet] = useState<Net>('mainnet');
  const [status, setStatus] = useState<StatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const loadStatus = () => {
    fetchJson<StatusData>('/api/status')
      .then(d => setStatus(d))
      .catch(() => setStatus(null))
      .finally(() => setStatusLoading(false));
  };

  useEffect(() => { loadStatus(); }, []);

  // Self-heal (v2.13.0): if the node is unreachable, keep polling every 4s so
  // the app recovers on its own the moment the server watchdog brings the
  // node back — the user never has to reload the page manually.
  const statusMissing = !status;
  useEffect(() => {
    if (!statusMissing) return;
    const t = setInterval(loadStatus, 4000);
    return () => clearInterval(t);
  }, [statusMissing]);

  const launched = status?.networks[net]?.launched ?? false;
  const founder = status?.networks[net]?.founder;
  const { data } = usePoll<{ launched: boolean; network: NetStats; mining: MiningStatus; market?: MarketTicker }>(
    async () => fetchJson(`/api/stats?net=${net}`),
    3000,
    [net, launched],
  );
  const n = data?.network;
  const mk = net === 'mainnet' ? data?.market : undefined;
  const mkUp = (mk?.change24h ?? 0) >= 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tab]);

  useEffect(() => {
    const onNav = (e: Event) => {
      const t = (e as CustomEvent<string>).detail;
      if (typeof t === 'string') setTab(t);
    };
    window.addEventListener('aman:navigate', onNav);
    return () => window.removeEventListener('aman:navigate', onNav);
  }, []);

  // If the selected network's stats stop reporting launched, go back to launch screen
  useEffect(() => {
    if (data && data.launched === false) loadStatus();
  }, [data?.launched]);

  const switchNet = (target: Net) => {
    setNet(target);
    if (MAINNET_ONLY.has(tab)) setTab('dashboard');
  };

  const onLaunched = (launchedNet: Net) => {
    loadStatus();
    setNet(launchedNet);
    setTab('dashboard');
  };

  return (
    <SessionProvider net={net} founder={founder}>
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <button onClick={() => setTab('dashboard')} className="flex items-center gap-2.5 shrink-0 group">
            <div className="rounded-xl bg-gradient-to-br from-emerald-500/25 to-emerald-500/5 ring-1 ring-emerald-500/40 p-2 text-emerald-400 group-hover:shadow-lg group-hover:shadow-emerald-500/20 transition">
              <ShieldCheck className="size-5" />
            </div>
            <div className="text-left">
              <div className="font-black text-sm leading-none">AmanChain</div>
              <Mono className="text-[0.6rem]">AMAN · Quantum-Safe Ledger</Mono>
            </div>
          </button>

          <div className="hidden md:flex items-center gap-4 text-xs">
            {net === 'mainnet' && mk?.hasMarket && (
              <button onClick={() => setTab('dex')} title="Live AMAN market — real AMM reserves" className="inline-flex items-center gap-1.5 rounded-full ring-1 px-2 py-1 font-black transition hover:opacity-80" style={{ background: mkUp ? 'rgba(52,211,153,0.08)' : 'rgba(251,113,133,0.08)', color: mkUp ? '#34d399' : '#fb7185', borderColor: 'transparent' }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/aman-logo.png" alt="AMAN" width={18} height={18} className="rounded-full" />
                <span dir="ltr">${mk.price.toFixed(4)}</span>
                <span dir="ltr">{mkUp ? '+' : ''}{(mk.change24h * 100).toFixed(2)}%</span>
              </button>
            )}
            {net === 'mainnet' && mk && !mk.hasMarket && (
              <button onClick={() => setTab('dex')} className="text-[0.68rem] rounded-full ring-1 ring-border px-2.5 py-1 text-muted-foreground hover:text-foreground transition font-bold">
                No market yet — open the DEX
              </button>
            )}
            {launched ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-emerald-400 font-bold"><span className="pulse-dot" /> Live</span>
                <span className="text-muted-foreground">Block <b className="text-foreground" dir="ltr">#{fmtInt(n?.height)}</b></span>
                <span className="text-muted-foreground">Difficulty <b className="text-foreground" dir="ltr">{fmtInt(n?.difficulty)}</b></span>
                <span className="text-muted-foreground">Mempool <b className="text-foreground" dir="ltr">{n?.mempoolSize ?? 0}</b></span>
              </>
            ) : (
              <span className="text-amber-400 font-bold">Network not launched</span>
            )}
          </div>

          {/* Account + Network switcher */}
          <div className="flex items-center gap-2">
            <AccountBar net={net} />
            <ThemeToggle />
            <div className="flex items-center rounded-xl ring-1 ring-input p-1 gap-1" role="tablist" aria-label="Network">
              {(['mainnet', 'testnet'] as Net[]).map(target => {
                const Icon = target === 'mainnet' ? Building2 : FlaskConical;
                const live = status?.networks[target]?.launched;
                const active = net === target;
                return (
                  <button
                    key={target}
                    role="tab"
                    aria-selected={active}
                    onClick={() => switchNet(target)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[0.7rem] font-bold transition ${active ? 'bg-primary/15 text-emerald-400 ring-1 ring-primary/40' : 'text-muted-foreground hover:text-foreground'}`}
                    title={status?.networks[target]?.description}
                  >
                    <Icon className="size-3.5" />
                    {target === 'mainnet' ? 'Mainnet' : 'Testnet'}
                    <span className={`size-1.5 rounded-full ${live ? 'bg-emerald-400' : 'bg-amber-400'}`} aria-label={live ? 'live' : 'offline'} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Navigation */}
        {launched && <NavTabs tab={tab} setTab={setTab} />}
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-6">
        {statusLoading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Connecting to the node…
          </div>
        ) : !status ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3 text-sm">
            <Loader2 className="size-6 animate-spin text-emerald-400" />
            <div className="font-bold">Reconnecting to the node…</div>
            <div className="text-[0.7rem] text-muted-foreground/80">The connection dropped — retrying automatically, no reload needed</div>
          </div>
        ) : !launched ? (
          <LaunchScreen net={net} networks={status?.networks ?? null} onLaunched={onLaunched} onSwitch={switchNet} />
        ) : (
          <>
            {tab === 'dashboard' && <Dashboard net={net} onNavigate={setTab} />}
            {tab === 'quantum' && <QuantumLab net={net} />}
            {tab === 'dex' && <DEX net={net} />}
            {tab === 'bridge' && <Bridge net={net} />}
            {tab === 'agents' && <AgentMarket net={net} />}
            {tab === 'investors' && <InvestorHub net={net} />}
            {tab === 'wallets' && <Wallets net={net} />}
            {tab === 'send' && <SendSection net={net} />}
            {tab === 'mining' && <Mining net={net} />}
            {tab === 'tokens' && <TokenLab net={net} />}
            {tab === 'staking' && <Staking net={net} />}
            {tab === 'explorer' && <Explorer net={net} />}
            {tab === 'developers' && <Developers net={net} />}
            {tab === 'admin' && <AdminConsole net={net} />}
            {tab === 'whitepaper' && <Whitepaper stats={n ?? null} />}
          </>
        )}
      </main>

      {/* Footer — sticks to the bottom */}
      <footer className="mt-auto border-t border-border/70 bg-background/80">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="size-4 text-emerald-500/70" />
            <span><b className="text-foreground">AmanChain</b> — every transaction carries a hybrid signature: ML-DSA-87 (NIST FIPS 204) + Ed25519 · SHA3 · deflationary fee burns · tiered staking up to 15% APR · AMT-20 native tokens</span>
          </div>
          <div className="text-[0.68rem] text-muted-foreground/80 text-center md:text-left">
            Demonstration network for research and education — assets are symbolic and not financial advice
          </div>
        </div>
      </footer>
      </div>
    </SessionProvider>
  );
}

// Navigation — Admin tab renders ONLY for the founder session on this network
function NavTabs({ tab, setTab }: { tab: string; setTab: (t: string) => void }) {
  const { isFounder } = useSession();
  const visible = TABS.filter(t => t.id !== 'admin' || isFounder);
  return (
    <nav className="max-w-7xl mx-auto px-4 pb-2 flex gap-1 overflow-x-auto" aria-label="Sections">
      {visible.map(t => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          aria-current={tab === t.id ? 'page' : undefined}
          className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-bold whitespace-nowrap transition ${tab === t.id
            ? 'bg-primary/15 text-emerald-400 ring-1 ring-primary/40'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'}`}
        >
          <t.icon className="size-3.5" /> {t.label}
        </button>
      ))}
    </nav>
  );
}

